/**
 * Direct port of `maps/biter_battles_v2/feeding_calculations.lua` plus the
 * write-back done by `Feeding.do_raw_feed` in `feeding.lua`.
 *
 * The port is deliberately literal — same loop, same order of operations, same
 * clamping — so that the app reproduces the game rather than an idealised
 * version of it. `src/model/model.test.ts` pins it against the expectations
 * in `tests/test-feeding.lua`.
 *
 * Both branches live here side by side: `calcFeedEffects` is the live scenario,
 * `calcFeedEffectsTesting` is `cojito_3v3_balance_testing`. Neither is expressed
 * in terms of the other, because they are not variants of one equation — the
 * test branch deletes the loop and replaces it with a closed form. `rulesets.ts`
 * is what picks between them; nothing else in the model chooses.
 */

import {
  BB_CONFIG,
  DEFAULTS,
  MUTAGEN_DISPLAY_SCALE,
  SCIENCE,
  TESTING_FEED,
  type ScienceKey,
} from './constants';

/** `math.round` as defined in utils/utils.lua:10. */
export function luaRound(num: number, idp = 0): number {
  const mult = 10 ** idp;
  return Math.floor(num * mult + 0.5) / mult;
}

/**
 * `get_instant_threat_player_count_modifier` — feeding_calculations.lua:10.
 * Ramps 125 -> 250 over the first 20 connected players, then saturates.
 */
export function playerCountModifier(currentPlayerCount: number): number {
  const minimumModifier = 125;
  const maximumModifier = 250;
  const playersForMaximum = 20;
  const gainPerPlayer = (maximumModifier - minimumModifier) / playersForMaximum;
  return Math.min(minimumModifier + gainPerPlayer * currentPlayerCount, maximumModifier);
}

/**
 * `u = 100 * min(evo, 1) + 1`. Both rate functions below are driven by this
 * clamped value, which is why everything freezes at its 100%-evo value once
 * evolution passes 1.0.
 */
export function clampedEvoTerm(evo: number): number {
  return Math.min(evo, 1) * 100 + 1;
}

/** dEvo/dMutagen — the "diminishing_modifier" applied to evolution. */
export function evoRate(evo: number): number {
  const u = clampedEvoTerm(evo);
  return 1 / 10 ** (u * 0.015) / (u * 0.5);
}

/**
 * dThreat/dMutagen before the player-count modifier. Past 100% evo the
 * `1 + k(E - 1)` term makes this grow without bound, which is what turns the
 * late game into a threat race.
 */
export function threatRate(evo: number): number {
  const u = clampedEvoTerm(evo);
  let rate = 1 / (0.2 + u * 0.016);
  if (evo > 1) rate *= 1 + (evo - 1) * BB_CONFIG.threatScaleFactorPastEvo100;
  return rate;
}

/**
 * `biter_health_factor` — feeding_calculations.lua:62.
 * Below 100% evo it is 1. Between 100% and 330% it follows the old
 * "reanimation chance" curve; above 330% it doubles every +70 evo points.
 */
export function biterHealthFactor(evo: number, maxReanimThresh = DEFAULTS.maxReanimThresh): number {
  if (evo < 3.3) {
    let reanimChance = Math.floor(Math.max(evo - 1, 0) * 100);
    reanimChance = (reanimChance / maxReanimThresh) * 100;
    reanimChance = Math.min(Math.floor(reanimChance), 90);
    return 1 / (1 - reanimChance / 100);
  }
  const k = Math.floor((evo - 3.3) / 0.7);
  const v0 = 10 * 2 ** k;
  const e0 = 3.3 + k * 0.7;
  return v0 + (v0 / 0.7) * (evo - e0);
}

export interface FeedEffects {
  /** Absolute evolution gained (0.37 means +37 evo points). */
  evoIncrease: number;
  /** Instant threat, already multiplied by the player-count modifier. */
  threatIncrease: number;
  /** Health factor at the resulting evolution. */
  biterHealthFactor: number;
  /**
   * `storage.bb_threat_income` after the feed, added once per second by
   * `on_tick` (main.lua:558, `tick % 60`).
   *
   * This is the `do_raw_feed` write-back rather than part of
   * `calc_feed_effects` on the live branch, where it is `evo * 25`. The test
   * branch moves it into `calc_feed_effects` as a returned `passive_threat`,
   * which is why it belongs on this shape for both.
   */
  passiveThreatPerSecond: number;
}

/**
 * Safety valve. The Lua loop is bounded by its own step caps (>=1% evo below
 * 100%, >=10% relative above), but a browser must never hang on a bad input.
 */
const MAX_ITERATIONS = 200_000;

/**
 * `Public.calc_feed_effects` — feeding_calculations.lua:25.
 *
 * @param initialEvo        uncapped evolution before the send
 * @param foodValue         mutagen per flask, i.e. `food_values[f].value * difficulty`
 * @param numFlasks         number of flasks in the send
 * @param currentPlayerCount connected players on north + south
 */
export function calcFeedEffects(
  initialEvo: number,
  foodValue: number,
  numFlasks: number,
  currentPlayerCount: number,
  maxReanimThresh = DEFAULTS.maxReanimThresh,
): FeedEffects {
  let threat = 0;
  let evo = initialEvo;
  let food = foodValue * numFlasks;
  const scalePastEvo100 = BB_CONFIG.threatScaleFactorPastEvo100;

  let iterations = 0;
  while (food > 0 && iterations < MAX_ITERATIONS) {
    iterations += 1;
    const clampedEvo = Math.min(evo, 1);
    const e2 = clampedEvo * 100 + 1;

    // SET EVOLUTION
    const evoDiminishing = 1 / 10 ** (e2 * 0.015) / (e2 * 0.5);
    const maxEvoGainPerIteration = evo < 1 ? 0.01 : evo / 10;
    const amountOfFoodThisIteration = Math.min(food, maxEvoGainPerIteration / evoDiminishing);
    evo = evo + amountOfFoodThisIteration * evoDiminishing;

    // ADD INSTANT THREAT — note this reads the *post-increment* evo.
    let threatDiminishing = 1 / (0.2 + e2 * 0.016);
    if (evo > 1) threatDiminishing *= 1 + (evo - 1) * scalePastEvo100;
    threat += amountOfFoodThisIteration * threatDiminishing;

    food -= amountOfFoodThisIteration;
  }

  threat *= playerCountModifier(currentPlayerCount);

  return {
    evoIncrease: evo - initialEvo,
    threatIncrease: threat,
    biterHealthFactor: biterHealthFactor(evo, maxReanimThresh),
    // `do_raw_feed` — feeding.lua:249. Reads the post-feed evolution.
    passiveThreatPerSecond: evo * DEFAULTS.threatIncomePerEvoPerSecond,
  };
}

/* -------------------------------------------------------------------------- */
/*  cojito_3v3_balance_testing                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Total mutagen that has been fed to reach `evo`, on the test branch.
 * `initial_food = (initial_evo ^ (1 / evo_power)) * evo_scale` —
 * feeding_calculations.lua:20. The branch reconstructs the food behind an
 * evolution on every feed, so evolution there is a pure function of the total
 * fed and nothing else: order, split and player count all drop out.
 */
export function testingMutagenForEvo(evo: number): number {
  if (evo <= 0) return 0;
  return evo ** (1 / TESTING_FEED.evoPower) * TESTING_FEED.evoScale;
}

/** `evo = (total_food / evo_scale) ^ evo_power` — feeding_calculations.lua:24. */
export function testingEvoForMutagen(mutagen: number): number {
  if (mutagen <= 0) return 0;
  return (mutagen / TESTING_FEED.evoScale) ** TESTING_FEED.evoPower;
}

/**
 * `Public.calc_feed_effects` on `cojito_3v3_balance_testing`.
 *
 * Three changes, all of them structural rather than a retune:
 *
 *   - the iterative diminishing-returns loop is gone, replaced by a power law;
 *   - instant threat is gone with it, and so is the player-count modifier
 *     (`threat_increase = 0`, unconditionally);
 *   - passive threat is no longer `evo * 25`. It is linear in the *total*
 *     mutagen fed, so income keeps climbing at the same rate per flask forever
 *     rather than flattening off with evolution.
 *
 * `biter_health_factor` is untouched, so it is the shared implementation.
 */
export function calcFeedEffectsTesting(
  initialEvo: number,
  foodValue: number,
  numFlasks: number,
  _currentPlayerCount: number,
  maxReanimThresh = DEFAULTS.maxReanimThresh,
): FeedEffects {
  const food = foodValue * numFlasks;
  const totalFood = testingMutagenForEvo(initialEvo) + food;
  const evo = testingEvoForMutagen(totalFood);

  return {
    evoIncrease: evo - initialEvo,
    threatIncrease: 0,
    biterHealthFactor: biterHealthFactor(evo, maxReanimThresh),
    passiveThreatPerSecond: totalFood * TESTING_FEED.smallBitersPerFood,
  };
}

/** A basket of flasks, keyed by science type. Missing keys count as zero. */
export type FlaskBasket = Partial<Record<ScienceKey, number>>;

/** Raw mutagen delivered by a basket at a given difficulty: `M = sum(v_f * d * n_f)`. */
export function basketMutagen(basket: FlaskBasket, difficulty: number): number {
  let total = 0;
  for (const key of Object.keys(basket) as ScienceKey[]) {
    const count = basket[key];
    if (count) total += SCIENCE[key].value * count;
  }
  return total * difficulty;
}

/** Flasks of one science type that carry `mutagen` raw mutagen at `difficulty`. */
export function mutagenToFlasks(mutagen: number, science: ScienceKey, difficulty: number): number {
  const per = SCIENCE[science].value * difficulty;
  return per > 0 ? mutagen / per : 0;
}

export const toDisplayMutagen = (rawMutagen: number): number => rawMutagen * MUTAGEN_DISPLAY_SCALE;
export const fromDisplayMutagen = (displayMutagen: number): number =>
  displayMutagen / MUTAGEN_DISPLAY_SCALE;

/** The threat tick is once per second — main.lua:558, `tick % 60 == 0`. */
export const THREAT_TICKS_PER_MINUTE = 60;

/**
 * Bisect any `calc_feed_effects` for the mutagen that carries `startEvo` to
 * `targetEvo`. Evolution is strictly increasing in mutagen under both branches,
 * so this is exact rather than approximate; a branch with a closed-form inverse
 * (the test branch has one) should use that instead and skip the search.
 */
export function bisectMutagenForEvo(
  feed: (initialEvo: number, mutagen: number) => number,
  targetEvo: number,
  startEvo = 0,
): number {
  if (targetEvo <= startEvo) return 0;
  let lo = 0;
  let hi = 1;
  // Expand until the target is bracketed.
  for (let i = 0; i < 200; i += 1) {
    if (feed(startEvo, hi) >= targetEvo) break;
    hi *= 2;
  }
  for (let i = 0; i < 80; i += 1) {
    const mid = (lo + hi) / 2;
    if (feed(startEvo, mid) < targetEvo) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}
